/**
 * Diffusion des changements de PROGRESSION dans toute l'app.
 *
 * Toutes les données serveur vivent dans le cache React Query (le « store »
 * de l'app, `lib/query-client.ts`, données gardées 5 min). Quand une activité
 * crédite de l'XP, des HORA ou change une maîtrise, on publie UN événement :
 * toutes les données qui en dépendent sont marquées périmées — les écrans
 * affichés se rechargent aussitôt, les autres au prochain affichage. Avant, la
 * page d'une SKILLCARD gardait l'ancien HORA jusqu'au rechargement.
 *
 * Ajouter une donnée dépendante = ajouter son préfixe de clé ici, une seule fois.
 */
import type { QueryClient } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';

export type ProgressEvent =
  /** Exercice validé (`finish_attempt`) : XP élève + HORA / maîtrise des cartes. */
  | 'exercise-finished'
  /** Série d'automatismes, test de pack, diagnostic, Warmup terminés. */
  | 'session-finished'
  /** Réponse à un live (HORA créditée à la carte de la question). */
  | 'live-answered'
  /** Cartes obtenues : achat, code cadeau, lot d'un live. */
  | 'cards-granted';

/** Ce que touche une progression (cartes, XP, tableau de bord, Mon année, Feed). */
const PROGRESS_KEYS = [
  'my-card-progress',
  'public-card',
  'student-skillcards',
  'my-card-count',
  'card-circle',
  'exercise-card-links',
  'student-profile',
  'success-rate',
  'my-progression',
  'curriculum-timeline',
  'pack-tests-state',
  'automatismes-state',
  'skill-diagnostic',
  'in-progress-exercises',
  'in-progress-attempts',
  'objectives-progress',
  'objective-progress',
  'objective-scope',
  'player-completed-attempts',
  'last-warmup',
  'news-attempts',
  'my-exercise-attempts',
  // la roue du jour s'ouvre en terminant un exercice
  'trousse-wheel',
  'trousse-collection',
] as const;

/** Droits d'accès en plus quand des cartes sont obtenues. */
const ACCESS_KEYS = [
  'skillcard-access',
  'skillcard-offers',
  'skillcard-deck',
  'my-programme-offers',
  'exercise-v2-player',
  // animation publiée : sa partie « À toi » s'ouvre avec l'accès à la carte (0666)
  'animations',
] as const;

export function progressKeysFor(event: ProgressEvent): readonly string[] {
  return event === 'cards-granted' ? [...PROGRESS_KEYS, ...ACCESS_KEYS] : PROGRESS_KEYS;
}

/** Publie un changement de progression : rafraîchit toutes les données concernées. */
export function publishProgress(event: ProgressEvent, client: QueryClient = queryClient): void {
  for (const key of progressKeysFor(event)) {
    void client.invalidateQueries({ queryKey: [key] });
  }
}
